import type { OutgoingMessage } from '@shared/types/ws'
import type { Socket } from 'socket.io-client'

import { io } from 'socket.io-client'

import type { OverlayTransport, TransportOptions } from './types'

/**
 * Транспорт поверх socket.io-client.
 *
 * Совместим с NestJS-gateway (@nestjs/platform-socket.io). Инкапсулирует
 * всю специфику Socket.IO, наружу отдавая тот же контракт, что ожидает
 * useWsConnection: нормализованный поток входящих JSON-строк (onMessage)
 * и типизированную отправку (send).
 *
 * ── Маппинг каналов ───────────────────────────────────────────────────────────
 *   Backend → Overlay:
 *     'overlay:event'    → overlay-событие (fireworks / snow / ...)
 *     'overlay:control'  → управляющее сообщение (register_plugin,
 *                          plugin_not_available, update_plugin_sound)
 *     'pong'             → ответ на client_ping
 *
 *   Overlay → Backend (по полю message.type):
 *     'client_ping'                         → emit 'client_ping'
 *     'ack'                                 → emit 'ack'
 *     'plugin_request'                      → emit 'plugin_request'
 *     'plugin_ready' | 'plugin_rejected'    → emit 'plugin_status'
 *
 * ── Авторизация ────────────────────────────────────────────────────────────────
 *   Хендшейк идёт с withCredentials: true — сессионная cookie уезжает
 *   автоматически. При connect_error с признаком авторизации вызывается
 *   onAuthError(); если он вернёт true (сессия обновлена) — повторяем
 *   подключение, иначе останавливаемся.
 *
 * ── Транспорт ────────────────────────────────────────────────────────────────
 *   Первая попытка — голый websocket (быстрее и дешевле для сервера при
 *   большинстве клиентов). Если самый первый connect_error пришёл до того,
 *   как соединение вообще устанавливалось (isFirstConnect ещё true) — это
 *   признак, что голый WS недоступен на сети клиента (типично для встроенного
 *   в OBS CEF-браузера за некоторыми firewall/VPN/антивирусами) — пересоздаём
 *   сокет только с polling, без авто-апгрейда на websocket дальше (чтобы не
 *   наступить на ту же грабли). Для большинства клиентов (где WS работает с
 *   первой попытки) этот fallback никогда не срабатывает и лишней HTTP-нагрузки
 *   на gateway не даёт.
 *
 * ── Heartbeat ─────────────────────────────────────────────────────────────────
 *   У Socket.IO есть собственный ping/pong на уровне Engine.IO, но прикладной
 *   client_ping сохранён ради совместимости с протоколом и health-индикатором.
 */
export class SocketIoTransport implements OverlayTransport {
  private socket: Socket | null = null
  private pingTimer: ReturnType<typeof setInterval> | null = null
  private destroyed = false
  private isFirstConnect = true
  private authRetrying = false
  /** Переключились ли уже на polling-only режим из-за недоступности голого WS. */
  private usingPollingFallback = false

  /**
   * @param origin     Origin Socket.IO сервера (например http://localhost:4000)
   * @param streamerId Идентификатор оверлея/стримера — уходит в query хендшейка
   * @param opts       Колбэки и параметры реконнекта
   */
  constructor(
    private readonly origin: string,
    private readonly streamerId: string,
    private readonly opts: TransportOptions,
  ) {}

  connect(): void {
    if (this.destroyed) return
    this.opts.onStatus('connecting')

    const socket = io(`${this.origin}/overlay`, {
      withCredentials: true,
      transports: this.usingPollingFallback ? ['polling'] : ['websocket'],
      query: { streamerId: this.streamerId },
      reconnection: true,
      reconnectionDelay: this.opts.baseRetryDelay,
      reconnectionDelayMax: this.opts.maxRetryDelay,
      // Бесконечные попытки — обрыв сети не должен "ронять" оверлей навсегда
      reconnectionAttempts: Infinity,
      timeout: 20_000,
    })
    this.socket = socket

    socket.on('connect', () => {
      if (this.destroyed) {
        socket.close()
        return
      }
      this.authRetrying = false
      this.opts.onStatus('connected')
      this.startPing()

      // onReconnect только при повторном подключении, не при первом
      if (!this.isFirstConnect) {
        this.opts.onReconnect?.()
      }
      this.isFirstConnect = false
    })

    socket.on('disconnect', () => {
      this.stopPing()
      if (this.destroyed) return
      this.opts.onStatus('disconnected')
      // Реконнектом управляет сам socket.io
    })

    socket.on('connect_error', (err: Error) => {
      this.opts.onStatus('error')
      if (this.destroyed) return

      // Самый первый connect_error до того, как соединение вообще устанавливалось —
      // признак, что голый WS недоступен на сети клиента (например встроенный
      // в OBS CEF-браузер за firewall/VPN). Переключаемся на polling-only и пересоздаём
      // сокет с нуля — один раз за весь жизненный цикл экземпляра транспорта.
      if (this.isFirstConnect && !this.usingPollingFallback) {
        this.usingPollingFallback = true
        // eslint-disable-next-line no-console
        console.warn('[overlay] websocket недоступен, переключаюсь на polling', err?.message)
        socket.removeAllListeners()
        socket.close()
        this.connect()
        return
      }

      void this.maybeHandleAuthError(err)
    })

    // ── Входящие каналы → нормализация в onMessage ──────────────────────────────
    socket.on('overlay:event', (event: unknown) => {
      this.emitNormalized(event)
    })

    socket.on('overlay:control', (msg: unknown) => {
      this.emitNormalized(msg)
    })

    socket.on('pong', () => {
      this.opts.onMessage(JSON.stringify({ type: 'pong' }))
    })
  }

  send(data: OutgoingMessage): void {
    const socket = this.socket
    if (!socket || !socket.connected) return

    switch (data.type) {
      case 'client_ping':
        socket.emit('client_ping', data)
        break
      case 'ack':
        socket.emit('ack', data)
        break
      case 'plugin_request':
        socket.emit('plugin_request', data)
        break
      case 'plugin_ready':
      case 'plugin_rejected':
        socket.emit('plugin_status', data)
        break
      default:
        // exhaustiveness guard — на случай расширения протокола
        socket.emit('message', data)
    }
  }

  destroy(): void {
    this.destroyed = true
    this.stopPing()
    this.socket?.removeAllListeners()
    this.socket?.close()
    this.socket = null
  }

  // ── Внутреннее ──────────────────────────────────────────────────────────────

  /** Сериализуем входящий объект и нормализуем в onMessage (как у native WS). */
  private emitNormalized(payload: unknown): void {
    if (payload == null || typeof payload !== 'object') return
    try {
      this.opts.onMessage(JSON.stringify(payload))
    } catch {
      // не сериализуется — игнорируем
    }
  }

  /**
   * Распознаёт ошибку авторизации. NestJS WsException / отбой сессии обычно
   * приходит как connect_error с message, содержащим 'auth'/'unauthorized'/'401'.
   */
  private async maybeHandleAuthError(err: Error): Promise<void> {
    const reason = `${err?.message ?? ''}`.toLowerCase()
    const looksLikeAuth =
      reason.includes('unauthor') ||
      reason.includes('auth') ||
      reason.includes('401') ||
      reason.includes('forbidden') ||
      reason.includes('session')

    if (!looksLikeAuth || !this.opts.onAuthError || this.authRetrying) return

    this.authRetrying = true
    // Прекращаем авто-реконнект socket.io пока обновляем сессию
    this.socket?.close()

    const recovered = await this.opts.onAuthError()
    if (this.destroyed) return

    if (recovered) {
      // Новая cookie уже установлена — переподключаемся с нуля
      this.isFirstConnect = false
      this.connect()
    } else {
      this.opts.onStatus('error')
      this.authRetrying = false
    }
  }

  private startPing(): void {
    if (this.opts.pingIntervalMs <= 0) return
    this.stopPing()
    this.pingTimer = setInterval(
      () => this.send({ type: 'client_ping', sentAt: Date.now() }),
      this.opts.pingIntervalMs,
    )
  }

  private stopPing(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer)
      this.pingTimer = null
    }
  }
}
